import React, { useState, useEffect } from "react"
import DataTable from "aldoo-ra/DataTable"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import Typography from "aldoo-ra/Typography"
import AddSubscriberDialog from "./AddSubscriberDialog"
import RestoreForwardRulesDialog from "./RestoreForwardRulesDialog"
import { ArrowUturnLeftIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import {
  GetForwardRules,
  CreateForwardRule,
  UpdateForwardRule,
  DeleteForwardRule,
} from "./forward-rules-api"

const CSForward = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isRestoreDialogOpen, setIsRestoreDialogOpen] = useState(false)
  const [editingRule, setEditingRule] = useState(null)
  const [loading, setLoading] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const handleAddRule = async (ruleData) => {
    try {
      setLoading(true)

      // Convert tags to simple string array
      const tags = ruleData.tags.map((tag) => tag.value || tag.name || tag)

      if (ruleData._id) {
        // Update existing rule
        const response = await UpdateForwardRule({
          _id: ruleData._id,
          subscriber: ruleData.subscriber,
          tags,
        })

        if (response.result) {
          showNotification({
            message: "Forward rule updated successfully",
            className: "bg-admin_primary text-admin_text_inv",
          })
          setRefreshKey((prev) => prev + 1)
        } else {
          throw new Error(response.error || "Failed to update rule")
        }
      } else {
        // Create new rule
        const response = await CreateForwardRule({
          subscriber: ruleData.subscriber,
          tags,
        })

        if (response.result) {
          showNotification({
            message: "Forward rule created successfully",
            className: "bg-admin_primary text-admin_text_inv",
          })
          setRefreshKey((prev) => prev + 1)
        } else {
          throw new Error(response.error || "Failed to create rule")
        }
      }

      setIsDialogOpen(false)
      setEditingRule(null)
    } catch (error) {
      showNotification({
        message: error.message || "Failed to save forward rule",
        className: "bg-admin_error text-white",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteRule = (id, subscriber) => {
    showPromptDialog({
      title: "Confirm Delete",
      message: `Are you sure you want to delete the forward rule for ${subscriber}?`,
      confirmText: "Delete",
      confirmVariant: "error_m",
      onConfirm: async () => {
        setLoading(true)
        try {
          const response = await DeleteForwardRule(id)

          if (response.result) {
            showNotification({
              message: "Forward rule deleted successfully",
              className: "bg-admin_primary text-admin_text_inv",
            })
            setRefreshKey((prev) => prev + 1)
          } else {
            throw new Error(response.error || "Failed to delete rule")
          }
        } catch (error) {
          showNotification({
            message: error.message || "Failed to delete forward rule",
            className: "bg-admin_error text-white",
          })
        } finally {
          setLoading(false)
        }
      },
    })
  }

  const handleEditRule = (rule) => {
    // Convert tags to proper format for dialog
    const formattedTags = rule.tags.map((tag) => ({
      value: tag,
      name: tag,
    }))
    setEditingRule({ ...rule, tags: formattedTags })
    setIsDialogOpen(true)
  }

  return (
    <div className="flex flex-col h-full p-6">
      {/* Header */}
      <div className="mb-6">
        {/* Title and Buttons */}
        <div className="w-full flex items-center justify-between mb-4">
          <Typography 
            component="h1"
            variant="admin_h3"
          >
            Forward Rules
          </Typography>

          <div className="flex gap-2">
            <Button
              variant="admin_secondary"
              onClick={() => setIsRestoreDialogOpen(true)}
            >
              <ArrowUturnLeftIcon className="h-5 w-5 mr-1" /> Restore
            </Button>

            <Button
              variant="admin_primary"
              onClick={() => {
                setEditingRule(null)
                setIsDialogOpen(true)
              }}
            >
              Add Rule
            </Button>
          </div>
        </div>

        <Typography 
          variant="admin_body_dim" 
          className="my-0"
        >
          When a ticket arrives with any of the specified tags, it will be automatically forwarded to the subscriber's email
        </Typography>
      </div>

      {/* Rules Table */}
      <DataTable
        localUserPreferencesKey="admin.customerSupport.forwardRules"
        variant="admin_browser"
        apiMethod={GetForwardRules}
        searchField="subscriber tags"
        sort={{ created: -1 }}
        refreshKey={refreshKey}
        preferredHeight="65vh"
        itemHeight="h-[60px]"
        noData={<Typography variant="admin_body">No forward rules configured</Typography>}
        renders={[
          {
            id: "subscriber",
            title: "Subscriber",
            render: (value, rule) => (
              <button
                onClick={() => handleEditRule(rule)}
                className="text-admin_primary hover:text-admin_primary/80 transition-colors cursor-pointer"
              >
                <Typography variant="admin_body" className="font-medium my-0">
                  {value}
                </Typography>
              </button>
            ),
          },
          {
            id: "tags",
            title: "Tags",
            render: (value, rule) => (
              <div className="flex flex-wrap gap-2">
                {value && value.length > 0 ? (
                  value.map((tag, index) => {
                    const tagText = typeof tag === "string" ? tag : (tag?.name || tag?.value || String(tag))
                    return (
                      <span
                        key={index}
                        className="px-3 py-1 bg-admin_primary/10 text-admin_primary rounded-full text-sm"
                      >
                        {tagText}
                      </span>
                    )
                  })
                ) : (
                  <Typography variant="admin_body" className="text-admin_text_secondary my-0">
                    No tags
                  </Typography>
                )}
              </div>
            ),
          },
          {
            id: "_id",
            title: "",
            render: (value, rule) => (
              <div className="flex justify-end gap-1">
                <Button
                  variant="admin_icon"
                  className="text-admin_text_secondary"
                  onClick={() => handleEditRule(rule)}
                  title="Edit rule"
                >
                  <PencilIcon className="w-5 h-5" />
                </Button>
                <Button
                  variant="admin_icon"
                  className="text-admin_text_secondary hover:!text-admin_error"
                  onClick={() => handleDeleteRule(value, rule.subscriber)}
                  title="Delete rule"
                >
                  <TrashIcon className="w-5 h-5" />
                </Button>
              </div>
            ),
          },
        ]}
      />

      {/* Add/Edit Subscriber Dialog */}
      <AddSubscriberDialog
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false)
          setEditingRule(null)
        }}
        onAdd={handleAddRule}
        editRule={editingRule}
      />

      {/* Restore Deleted Rules Dialog */}
      <RestoreForwardRulesDialog
        isOpen={isRestoreDialogOpen}
        onClose={() => setIsRestoreDialogOpen(false)}
        parentRefreshKey={refreshKey}
        onRulesRestored={() => setRefreshKey((prev) => prev + 1)}
      />
    </div>
  )
}

export default CSForward
